
import { z } from "zod";

const severityConfigSchema = z.object({
  action: z.enum(['enforce_immediate', 'enforce_timeframe', 'notify_only', 'no_action']),
  timeframeValue: z.number().min(1).optional(),
  timeframeUnit: z.enum(['hours', 'days', 'weeks']).optional(),
  timezone: z.string().optional(),
  timeOfDay: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/).optional(),
}).refine((data) => {
  if (data.action === 'enforce_timeframe') {
    return data.timeframeValue && data.timeframeUnit && data.timezone && data.timeOfDay;
  }
  return true;
}, {
  message: "Timeframe, timezone, and time of day are required when enforcing on a timeframe"
});

export const patchPolicySchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  schedule_type: z.enum(["immediate", "scheduled", "manual"]),
  restart_required: z.boolean().default(false),
  defer_max_times: z.number().min(0).default(3),
  defer_duration_minutes: z.number().min(0).default(60),
  enabled: z.boolean().default(true),
  max_os_version: z.string().optional(),
  update_method: z.enum(["default", "custom"]).default("default"),
  force_install_days: z.number().min(0).max(365).default(90),
  allowed_days: z.array(z.string()).default([]),
  show_notifications: z.boolean().default(true),
  notifications_timing: z.enum(["all", "one_hour_before"]).default("all"),
  allow_major_updates: z.boolean().default(true),
  allow_minor_updates: z.boolean().default(true),
  allow_security_updates: z.boolean().default(true),
  auto_download: z.boolean().default(true),
  auto_install_os_updates: z.boolean().default(false),
  auto_install_security_updates: z.boolean().default(true),
  enable_rsr_installation: z.boolean().default(false),
  enable_rsr_rollback: z.boolean().default(false),
  support_article_url: z.string().url().optional().or(z.literal("")),
  // New settings fields
  smtp_host: z.string().optional(),
  smtp_port: z.number().min(1).max(65535).optional(),
  smtp_username: z.string().optional(),
  smtp_password: z.string().optional(),
  smtp_from_address: z.string().email().optional(),
  apns_certificate: z.string().optional(),
  apns_certificate_password: z.string().optional(),
  abm_token: z.string().optional(),
  abm_organization_id: z.string().optional(),
  abm_server_token: z.string().optional(),
  // Vulnerability remediation fields
  critical: severityConfigSchema.optional(),
  high: severityConfigSchema.optional(),
  medium: severityConfigSchema.optional(),
  low: severityConfigSchema.optional(),
  applyToActivelyExploitedOnly: z.boolean().default(false),
  vulnerabilityRestartBehavior: z.enum(['immediate', 'defer_available', 'scheduled']).default('defer_available'),
  vulnerabilityNotificationRecipients: z.array(z.string().email()).default([]),
});

export type PatchPolicyFormValues = z.infer<typeof patchPolicySchema>;
